/**
 * What the Reports screen shows, from the server's query response. There is NO client money arithmetic
 * here: a cell is `series[i]` or an owner `cells[j]`, a row total is `amount`, a column total is
 * `total.series[i]` or `columns[j].amount`, the grand total is `total.amount`. The one accumulator is `stackLayout`, and it works in pixels.
 */
import { type CategoryMap } from '../cashflow/cashflowCategories'
import { unitStart, type Granularity } from '../cashflow/cashflowPeriod'
import { detailPath, type DetailScope } from '../cashflow/cashflowScope'
import type { QueryDimension, QueryRow, ReportQuery } from '../cashflow/cashflowWire'
import type { LocalDate } from '../../platform/localDate'
import { accountLabel } from '../transactions/txnDisplay'
import { columnLabel, type Columns } from './reportSpec'

/** Four series fills (`--s1`…`--s4`), the render's count and the owner's example (P11). */
export const CHART_SERIES = 4
export type SeriesIndex = 1 | 2 | 3 | 4

const SERIES: readonly SeriesIndex[] = [1, 2, 3, 4]

/** Index 5 is the server's "Other", 6 is Uncategorised (a neutral fill). Never a table row's colour. */
export type ChartIndex = SeriesIndex | 5 | 6
export type ChartSeries = { id: string; label: string; index: ChartIndex; cents: number[] }

/**
 * LAYOUT ONLY (P7): pixel offsets for stacked segments. A stack cannot be drawn without placing each
 * segment on top of the last, so this adds the values it is given. With a pixel scale the result is a
 * coordinate; ReportChart also calls it once with an identity scale, where the result is a cumulative
 * cents extent used only to size the y-domain. Either way it is never rendered as text and never
 * announced. Every total on screen is a server field.
 * Positives stack up from zero and negatives down from zero, each on its own side (B5).
 */
export function stackLayout(values: readonly number[], y: (v: number) => number): { y0: number; y1: number }[] {
  let up = 0
  let down = 0
  return values.map((v) => {
    if (v >= 0) {
      const y0 = y(up)
      up += v
      return { y0, y1: y(up) }
    }
    const y0 = y(down)
    down += v
    return { y0, y1: y(down) }
  })
}

// ---------------------------------------------------------------------------------------------------------
// The view over GET /reports/query. Same rule: every figure is a server field, nothing is added.

export type Axis =
  | { kind: 'period'; labels: string[]; current: number | null }
  | { kind: 'owner'; ids: string[]; labels: string[] }

export type QueryTableRow =
  | { kind: 'row'; key: string; label: string; detail: string | null; cells: number[]; totalCents: number; series: ChartIndex | null }
  | { kind: 'other'; count: number; noun: string; cells: number[]; totalCents: number }
  | { kind: 'none'; label: string; cells: number[]; totalCents: number }
  | { kind: 'total'; cells: number[]; totalCents: number }

const NOUNS: Record<QueryDimension, readonly [string, string]> = {
  category: ['category', 'categories'],
  group: ['group', 'groups'],
  merchant: ['merchant', 'merchants'],
  account: ['account', 'accounts'],
  owner: ['owner', 'owners'],
  tag: ['tag', 'tags'],
}

export function dimensionNoun(rows: QueryDimension, n: number): string {
  return NOUNS[rows][n === 1 ? 0 : 1]
}

/** Two owners can share a name ("Member"); a screen reader needs the columns told apart. */
function distinct(names: string[]): string[] {
  const seen = new Map<string, number>()
  return names.map((n) => {
    const k = (seen.get(n) ?? 0) + 1
    seen.set(n, k)
    return k === 1 ? n : `${n} (${k})`
  })
}

export function queryAxis(q: ReportQuery, spansYears: boolean): Axis {
  if (q.columns !== null) {
    return { kind: 'owner', ids: q.columns.map((c) => c.id), labels: distinct(q.columns.map((c) => c.name)) }
  }
  const labels = q.periods.map((p) => columnLabel(p, q.granularity, spansYears))
  const i = q.periods.findIndex((p) => !p.complete && p.start <= q.window.today && q.window.today <= p.end)
  return { kind: 'period', labels, current: i === -1 ? null : i }
}

/** The chart and the table are readings of one dataset: the same bucket dates, or the same owners in order. */
export function sameAxis(a: ReportQuery, b: ReportQuery): boolean {
  if (a.columns !== null || b.columns !== null) {
    return a.columns !== null && b.columns !== null && a.columns.length === b.columns.length && a.columns.every((c, i) => c.id === b.columns?.[i]?.id)
  }
  return a.periods.length === b.periods.length && a.periods.every((p, i) => p.start === b.periods[i]?.start && p.end === b.periods[i]?.end)
}

export function isEmptyQuery(q: ReportQuery): boolean {
  return q.rows.length === 0 && q.remainder.rowCount === 0
}

/** One value per column of the axis: `series` on a period axis, `cells` on an owner axis. */
function cellsOf(q: ReportQuery, r: { seriesCents: number[]; cells: { amountCents: number }[] | null }): number[] {
  return q.columns === null ? r.seriesCents : (r.cells ?? []).map((c) => c.amountCents)
}

function rowLabel(r: QueryRow, rowsBy: QueryDimension, names: CategoryMap): string {
  if (r.isNone) return r.name
  if ((rowsBy === 'category' || rowsBy === 'group') && r.id !== null) return names.get(r.id)?.name ?? r.name
  if (rowsBy === 'account') return accountLabel({ name: r.name, mask: r.mask })
  return r.name
}

export function queryRows(q: ReportQuery, chart: ReportQuery | null, names: CategoryMap): QueryTableRow[] {
  const drawn = new Map<string, ChartIndex>()
  for (const s of chart === null ? [] : queryChartSeries(chart, names)) drawn.set(s.id, s.index)
  const out: QueryTableRow[] = []
  for (const r of q.rows) {
    if (r.isNone) continue
    const parentId = q.rowsBy === 'category' && r.id !== null ? (names.get(r.id)?.parentId ?? null) : null
    out.push({
      kind: 'row',
      key: r.id ?? 'none',
      label: rowLabel(r, q.rowsBy, names),
      detail: parentId === null ? null : (names.get(parentId)?.name ?? null),
      cells: cellsOf(q, r),
      totalCents: r.amountCents,
      series: r.id === null ? null : (drawn.get(r.id) ?? null),
    })
  }
  if (q.remainder.rowCount > 0) {
    out.push({
      kind: 'other',
      count: q.remainder.rowCount,
      noun: dimensionNoun(q.rowsBy, 2),
      cells: cellsOf(q, q.remainder),
      totalCents: q.remainder.amountCents,
    })
  }
  for (const r of q.rows) {
    if (r.isNone) out.push({ kind: 'none', label: r.name, cells: cellsOf(q, r), totalCents: r.amountCents })
  }
  out.push({
    kind: 'total',
    cells: q.columns === null ? q.total.seriesCents : q.columns.map((c) => c.amountCents),
    totalCents: q.total.amountCents,
  })
  return out
}

/**
 * The chart's series, from the `limit=4` response: every named row (whatever its sign, so a stacked column
 * reaches the Total), then the server's remainder as "Other", then the none row in a neutral fill.
 */
export function queryChartSeries(q: ReportQuery, names: CategoryMap): ChartSeries[] {
  const out: ChartSeries[] = []
  for (const r of q.rows) {
    if (r.isNone) continue
    const index = SERIES[out.length]
    if (index === undefined || r.id === null) break
    out.push({ id: r.id, label: rowLabel(r, q.rowsBy, names), index, cents: cellsOf(q, r) })
  }
  const n = q.remainder.rowCount
  if (n > 0) {
    out.push({ id: 'other', label: `Other (${n} ${dimensionNoun(q.rowsBy, n)})`, index: 5, cents: cellsOf(q, q.remainder) })
  }
  for (const r of q.rows) {
    if (r.isNone) out.push({ id: 'none', label: r.name, index: 6, cents: cellsOf(q, r) })
  }
  return out
}

export const TAGS_OVERLAP_NOTE =
  'A transaction with more than one tag counts under each, so the tag rows add up to more than the Total.'

/** The table asked for the most rows the server allows; past that the rest are the server's "Other". A count, not money. */
export function tableNote(q: ReportQuery): string | null {
  if (q.remainder.rowCount === 0) return null
  const shown = q.rows.filter((r) => !r.isNone).length
  return `Top ${shown} of ${shown + q.remainder.rowCount} ${dimensionNoun(q.rowsBy, 2)}. The rest are summed in Other.`
}

const DRILL_KIND: Partial<Record<QueryDimension, 'category' | 'group' | 'merchant'>> = {
  category: 'category',
  group: 'group',
  merchant: 'merchant',
}

/**
 * Where a report row opens: the Cashflow drill-down for its category, group or merchant, written by the
 * same `detailPath` the Cashflow rows use. The detail page shows ONE period, so the Reports columns
 * carry as its granularity (owner columns read as month) and the period is the one the range ends in.
 * A row that spans several periods opens the detail with the LAST period selected. Filters are not carried,
 * so the caller draws no link under one. Uncategorised (M3-R6), Other, Total and the dimensions with no scope are not links: null.
 */
export function drillPath(rowsBy: QueryDimension, row: QueryTableRow, columns: Columns, end: LocalDate, today: LocalDate): string | null {
  const kind = DRILL_KIND[rowsBy]
  if (kind === undefined || row.kind !== 'row') return null
  const granularity: Granularity = columns === 'quarter' ? 'quarter' : 'month'
  const target: DetailScope = { kind, id: row.key }
  return detailPath(target, { granularity, period: unitStart(end, granularity) }, today)
}
